// @ts-nocheck
import { useState } from 'react';
import {
  Box,
  Checkbox,
  Group,
  NumberInput,
  PasswordInput,
  Radio,
  Select,
  SimpleGrid,
  Slider,
  Stack,
  Switch,
  Text as MantineText,
  TextInput,
  Textarea,
  Title,
  Code,
} from '@mantine/core';
import {
  IconAt,
  IconLock,
  IconPhone,
  IconSearch,
  IconUser,
} from '@tabler/icons-react';
import { Button, Progress, Text, Card } from '../../components/ui';

export default function FormsPage() {
  const [sliderValue, setSliderValue] = useState(50);
  const [switchChecked, setSwitchChecked] = useState(true);
  const [radioValue, setRadioValue] = useState('option1');

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Form Components</Title>
        <MantineText c="dimmed" size="sm">
          Comprehensive form inputs, controls, and interactive elements with examples
        </MantineText>
      </Stack>

      {/* Overview */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Form Components Overview</Title>
          <MantineText size="sm" c="dimmed">
            This page demonstrates all available form components including text inputs, selects, checkboxes, radios, switches, sliders, and more.
          </MantineText>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Available Components:</MantineText>
            <MantineText size="xs" c="dimmed" component="div">
              • TextInput, PasswordInput, NumberInput, Textarea
              <br />
              • Select, MultiSelect with search capabilities
              <br />
              • Checkbox, Radio, Switch with descriptions
              <br />
              • Slider with marks and Progress bars
              <br />
              • All form states (default, disabled, error, required)
            </MantineText>
          </Stack>
        </Stack>
      </Card>

      {/* Text Inputs */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Text Inputs</Title>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label="Username"
              placeholder="Enter your username"
              leftSection={<IconUser size={16} />}
            />
            <TextInput
              label="Email"
              placeholder="you@example.com"
              leftSection={<IconAt size={16} />}
            />
            <PasswordInput
              label="Password"
              placeholder="Enter password"
              leftSection={<IconLock size={16} />}
            />
            <TextInput
              label="Phone"
              placeholder="+1 (555) 000-0000"
              leftSection={<IconPhone size={16} />}
            />
          </SimpleGrid>

          <TextInput
            label="Search"
            placeholder="Search..."
            leftSection={<IconSearch size={16} />}
            description="Type to search across all items"
          />

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<TextInput
  label="Username"
  placeholder="Enter your username"
  leftSection={<IconUser size={16} />}
/>

<PasswordInput
  label="Password"
  placeholder="Enter password"
  leftSection={<IconLock size={16} />}
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Input States */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Input States</Title>

          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <TextInput label="Default" placeholder="Default state" />
            <TextInput label="Disabled" placeholder="Disabled input" disabled />
            <TextInput label="With Error" placeholder="Error state" error="This field is required" />
            <TextInput
              label="With Description"
              placeholder="Described input"
              description="This is a helpful description"
            />
            <TextInput label="Required" placeholder="Required field" required />
            <TextInput label="Read Only" value="Read only value" readOnly />
          </SimpleGrid>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<TextInput label="Default" placeholder="Default state" />
<TextInput label="Disabled" placeholder="Disabled input" disabled />
<TextInput
  label="With Error"
  placeholder="Error state"
  error="This field is required"
/>
<TextInput
  label="Required"
  placeholder="Required field"
  required
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Select & Dropdowns */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Select Inputs</Title>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Select
              label="Country"
              placeholder="Select country"
              data={[
                { value: 'us', label: 'United States' },
                { value: 'uk', label: 'United Kingdom' },
                { value: 'ca', label: 'Canada' },
                { value: 'au', label: 'Australia' },
                { value: 'de', label: 'Germany' },
              ]}
              searchable
            />
            <Select
              label="Priority"
              placeholder="Select priority"
              data={[
                { value: 'low', label: 'Low Priority' },
                { value: 'medium', label: 'Medium Priority' },
                { value: 'high', label: 'High Priority' },
                { value: 'urgent', label: 'Urgent' },
              ]}
            />
          </SimpleGrid>

          <NumberInput
            label="Quantity"
            placeholder="Enter quantity"
            min={0}
            max={100}
            defaultValue={1}
          />

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Select
  label="Country"
  placeholder="Select country"
  data={[
    { value: 'us', label: 'United States' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'ca', label: 'Canada' },
  ]}
  searchable
/>

<NumberInput
  label="Quantity"
  placeholder="Enter quantity"
  min={0}
  max={100}
  defaultValue={1}
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Textarea */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Textarea</Title>

          <Textarea
            label="Description"
            placeholder="Enter a detailed description..."
            minRows={3}
            maxRows={6}
            autosize
          />

          <Textarea
            label="Comments"
            placeholder="Add your comments..."
            minRows={2}
            description="Maximum 500 characters"
          />

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Textarea
  label="Description"
  placeholder="Enter a detailed description..."
  minRows={3}
  maxRows={6}
  autosize
/>

<Textarea
  label="Comments"
  placeholder="Add your comments..."
  minRows={2}
  description="Maximum 500 characters"
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Toggles & Switches */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Toggles & Switches</Title>

          <Group gap="xl">
            <Switch
              label="Enable notifications"
              checked={switchChecked}
              onChange={(e) => setSwitchChecked(e.currentTarget.checked)}
            />
            <Switch label="Dark mode" />
            <Switch label="Auto-save" defaultChecked />
          </Group>

          <Stack gap="xs">
            <Switch
              label="Email notifications"
              description="Receive email updates about your account"
              size="md"
            />
            <Switch
              label="Push notifications"
              description="Get notified on your mobile device"
              size="md"
            />
            <Switch
              label="Weekly digest"
              description="Receive a summary email every week"
              size="md"
              disabled
            />
          </Stack>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Switch label="Enable notifications" />
<Switch label="Dark mode" defaultChecked />

<Switch
  label="Email notifications"
  description="Receive email updates about your account"
  size="md"
/>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Checkboxes & Radio */}
      <Card withBorder p="lg">
        <Stack gap="lg">
          <Title order={4}>Checkboxes & Radio Buttons</Title>

          <Box>
            <MantineText size="sm" fw={500} mb="xs">
              Checkboxes
            </MantineText>
            <Group gap="lg">
              <Checkbox label="Option A" />
              <Checkbox label="Option B" defaultChecked />
              <Checkbox label="Option C" />
              <Checkbox label="Disabled" disabled />
            </Group>
          </Box>

          <Box>
            <MantineText size="sm" fw={500} mb="xs">
              Radio Group
            </MantineText>
            <Radio.Group value={radioValue} onChange={setRadioValue}>
              <Group gap="lg">
                <Radio value="option1" label="First option" />
                <Radio value="option2" label="Second option" />
                <Radio value="option3" label="Third option" />
              </Group>
            </Radio.Group>
          </Box>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Checkbox label="Option A" />
<Checkbox label="Option B" defaultChecked />
<Checkbox label="Disabled" disabled />

<Radio.Group value={radioValue} onChange={setRadioValue}>
  <Group gap="lg">
    <Radio value="option1" label="First option" />
    <Radio value="option2" label="Second option" />
    <Radio value="option3" label="Third option" />
  </Group>
</Radio.Group>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Slider */}
      <Card withBorder p="lg">
        <Stack gap="lg">
          <Title order={4}>Sliders & Progress</Title>

          <Box>
            <MantineText size="sm" fw={500} mb="xs">
              Volume: {sliderValue}%
            </MantineText>
            <Slider
              value={sliderValue}
              onChange={setSliderValue}
              marks={[
                { value: 0, label: '0%' },
                { value: 50, label: '50%' },
                { value: 100, label: '100%' },
              ]}
            />
          </Box>

          <Box>
            <MantineText size="sm" fw={500} mb="xs">
              Gradient Progress
            </MantineText>
            <Stack gap="xs">
              <Progress gradientVariant="primary" value={75} />
              <Progress gradientVariant="secondary" value={50} striped />
              <Progress gradientVariant="tertiary" value={25} striped animated />
            </Stack>
          </Box>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Slider
  value={sliderValue}
  onChange={setSliderValue}
  marks={[
    { value: 0, label: '0%' },
    { value: 50, label: '50%' },
    { value: 100, label: '100%' },
  ]}
/>

<Progress gradientVariant="primary" value={75} />
<Progress gradientVariant="secondary" value={50} striped />
<Progress gradientVariant="tertiary" value={25} striped animated />`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Gradient Buttons */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Gradient Buttons</Title>

          <Group gap="md">
            <Button gradientVariant="primary">Primary</Button>
            <Button gradientVariant="secondary">Secondary</Button>
            <Button gradientVariant="tertiary">Tertiary</Button>
          </Group>

          <Group gap="md">
            <Button gradientVariant="success" hoverEffect="glow">
              Success (Glow)
            </Button>
            <Button gradientVariant="info" hoverEffect="lift">
              Info (Lift)
            </Button>
            <Button gradientVariant="warning" hoverEffect="scale">
              Warning (Scale)
            </Button>
            <Button gradientVariant="danger" hoverEffect="none">
              Danger (None)
            </Button>
          </Group>

          <Group gap="md">
            <Button gradientVariant="primary" size="xs">
              Extra Small
            </Button>
            <Button gradientVariant="primary" size="sm">
              Small
            </Button>
            <Button gradientVariant="primary" size="md">
              Medium
            </Button>
            <Button gradientVariant="primary" size="lg">
              Large
            </Button>
            <Button gradientVariant="primary" size="xl">
              Extra Large
            </Button>
          </Group>

          <Stack gap="xs">
            <MantineText size="sm" fw={500}>Code:</MantineText>
            <Code block language="tsx">
{`<Button gradientVariant="primary">Primary</Button>
<Button gradientVariant="secondary">Secondary</Button>
<Button gradientVariant="tertiary">Tertiary</Button>

<Button gradientVariant="success" hoverEffect="glow">
  Success (Glow)
</Button>

<Button gradientVariant="primary" size="lg">
  Large
</Button>`}
            </Code>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}
